import React, { useState, useEffect, useRef } from 'react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import { useNavigate } from 'react-router-dom'
import config, { axiosInstance } from '../Config'
import Pagination, { PerPageSelect } from '../../components/Pagination'
import Loader from '../Loader'
import swal from 'sweetalert'
import { toast } from 'react-toastify'
import { usePermissionData } from '../../AppContext'

const avatarColors = ['#A44CD7', '#0CACC2', '#F55145', '#F9BE22', '#8A3BBD', '#19C37D', '#5A79DF', '#EC2434']
const getInitials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

const TutorIndex = () => {
    const [tutors, setTutors] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [openDropdown, setOpenDropdown] = useState(null)

    const debounceRef = useRef(null)
    const navigate = useNavigate()
    const { checkAccess } = usePermissionData()

    useEffect(() => {
        document.title = 'Tutors | Mindora'
        const handleOutside = (e) => {
            if (!e.target.closest('.tutor-action-wrap')) setOpenDropdown(null)
        }
        document.addEventListener('mousedown', handleOutside)
        return () => document.removeEventListener('mousedown', handleOutside)
    }, [])

    useEffect(() => {
        fetchTutors(page, search)
    }, [page, itemsPerPage])

    const fetchTutors = (pg = page, q = search) => {
        setLoading(true)
        axiosInstance.get(`/tutors?page=${pg}&limit=${itemsPerPage}&search=${encodeURIComponent(q)}`)
            .then(res => {
                const data = res.data?.data ?? res.data
                setTutors(Array.isArray(data) ? data : [])
                const pag = res.data?.pagination ?? {}
                setTotalItems(pag.total ?? 0)
                setTotalPages(pag.totalPages ?? 1)
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false))
    }

    const onSearch = (e) => {
        const q = e.target.value
        setSearch(q)
        setPage(1)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => fetchTutors(1, q), 400)
    }

    const onDelete = (id) => {
        setOpenDropdown(null)
        swal({
            title: 'Are you sure?',
            text: 'You want to delete this tutor?',
            icon: 'warning',
            buttons: true,
            dangerMode: true,
        }).then(willDelete => {
            if (willDelete) {
                axiosInstance.delete(`/tutors/${id}`)
                    .then(() => {
                        swal('', 'Tutor deleted successfully.', 'success')
                        fetchTutors()
                    })
                    .catch(err => toast.error(err.response?.data?.message || 'Failed to delete tutor'))
            }
        })
    }

    const onToggleStatus = (tutor) => {
        setOpenDropdown(null)
        const optimistic = tutors.map(t => t.id === tutor.id ? { ...t, status: !t.status } : t)
        setTutors(optimistic)
        axiosInstance.patch(`/tutors/${tutor.id}/status`)
            .catch(err => {
                setTutors(tutors)
                toast.error(err.response?.data?.message || 'Failed to update status')
            })
    }

    return (
        <ContainerDashboard title="Tutors" description="Manage your tutors">
            {loading && <Loader />}
            <section className="ps-dashboards" id="homepages">
                <div className="row justify-content-between align-items-center">
                    <div className="col-md-12">
                        <div className="p-4 py-5 bg-gradientt rounded-4" style={{ minHeight: '75vh' }}>

                            <div className='d-md-flex justify-content-between align-items-center mb-4'>
                                <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
                                    <div className="ps-form--search-simple">
                                        <input className="form-control bg-white" type="text"
                                            value={search} onChange={onSearch} placeholder="Search Tutors..." />
                                        <button type='button' style={{ right: '30px' }}>
                                            <i className="icon icon-magnifier"></i>
                                        </button>
                                    </div>
                                    <PerPageSelect itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} setPage={setPage} />
                                </div>
                                <div className='d-flex gap-2'>
                                    {checkAccess(15, 2) && (
                                        <button type="button" className="button btn-green color-white"
                                            onClick={() => navigate(`${config.appUrl}add-tutor`)}>
                                            <i className='icon-plus me-2 fs-5'></i>Add Tutor
                                        </button>
                                    )}
                                    <button type="button" className="button"
                                        style={{ background: '#1a1a2e', color: '#fff', borderRadius: '12px' }}
                                        onClick={() => navigate(-1)}>
                                        <i className='icon-arrow-left me-2'></i>Back
                                    </button>
                                </div>
                            </div>

                            <div className='table-responsive'>
                                <table className='jean-table table'>
                                    <tbody>
                                        {tutors.map((tutor, index) => (
                                            <tr key={tutor.id}>
                                                <td width={60}>
                                                    {tutor.profileImage
                                                        ? <img src={tutor.profileImage} alt={tutor.name}
                                                            className="rounded-circle" style={{ width: 46, height: 46, objectFit: 'cover' }} />
                                                        : <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{ width: 46, height: 46, background: avatarColors[index % avatarColors.length], color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                                                            {getInitials(tutor.name)}
                                                          </div>
                                                    }
                                                </td>

                                                <td>
                                                    <div className='therapist-info'>
                                                        <p className='mb-1' style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }}>Name</p>
                                                        <p className='mb-0 fw-bold'>{tutor.name}</p>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className='therapist-info'>
                                                        <p className='mb-1' style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }}>Email</p>
                                                        <p className='mb-0'>{tutor.email}</p>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className='therapist-info'>
                                                        <p className='mb-1' style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }}>Gender</p>
                                                        <p className='mb-0'>{tutor.gender}</p>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className='therapist-info'>
                                                        <p className='mb-1' style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }}>Qualification</p>
                                                        <p className='mb-0'>{tutor.qualification}</p>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div>
                                                        <p className='mb-1' style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }}>Status</p>
                                                        <div className='form-check form-switch mb-0'>
                                                            <input className='form-check-input' type='checkbox' role='switch'
                                                                checked={!!tutor.status}
                                                                onChange={() => checkAccess(15, 3) && onToggleStatus(tutor)}
                                                                disabled={!checkAccess(15, 3)}
                                                                style={{ cursor: checkAccess(15, 3) ? 'pointer' : 'not-allowed', width: 40, height: 22 }} />
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className='pe-3'>
                                                    <div className='tutor-action-wrap position-relative'>
                                                        <button className='tutor-action-btn'
                                                            onClick={() => setOpenDropdown(openDropdown === tutor.id ? null : tutor.id)}>
                                                            Actions <i className={`bi bi-chevron-${openDropdown === tutor.id ? 'up' : 'down'} ms-1`} style={{ fontSize: '11px' }}></i>
                                                        </button>
                                                        {openDropdown === tutor.id && (
                                                            <div className='tutor-action-dropdown'>
                                                                {checkAccess(15, 3) && (
                                                                    <div className='tutor-action-item'
                                                                        onClick={() => { setOpenDropdown(null); navigate(`${config.appUrl}edit-tutor/${tutor.id}`) }}>
                                                                        <i className='icon-pen'></i> Edit Tutor
                                                                    </div>
                                                                )}
                                                                <div className='tutor-action-item'
                                                                    onClick={() => { setOpenDropdown(null); navigate(`${config.appUrl}tutors/slots/${tutor.id}`) }}>
                                                                    <i className='bi bi-calendar3'></i> View Sessions
                                                                </div>
                                                                <div className='tutor-action-item'
                                                                    onClick={() => { setOpenDropdown(null); navigate(`${config.appUrl}tutors/availability/${tutor.id}`) }}>
                                                                    <i className='bi bi-clock'></i> Availability
                                                                </div>
                                                                {checkAccess(15, 4) && (
                                                                    <div className='tutor-action-item text-danger'
                                                                        onClick={() => onDelete(tutor.id)}>
                                                                        <i className='icon-trash2'></i> Delete Tutor
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {!loading && tutors.length === 0 && (
                                            <tr>
                                                <td colSpan={7}>
                                                    <div className="alert alert-success ps-4">No Tutors Found!</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                setPage={setPage}
                                totalItems={totalItems}
                                showingCount={tutors.length}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </ContainerDashboard>
    )
}

export default TutorIndex
